import React, { useState, useEffect } from 'react';
import { BarChart3 } from 'lucide-react';
import { AccountPosition } from '../types';
import schwabService from '../services/schwabService';

interface OptionsInformationProps {
  position: AccountPosition;
  optionMarketData: Record<string, any>;
  formatCurrency: (value: number) => string;
  formatPercentage: (value: number) => string;
}

interface OptionMarketData {
  symbol: string;
  strike: number;
  expiration: string;
  contractType: string;
  bid: number;
  ask: number;
  last: number;
  volume: number;
  openInterest: number;
  impliedVolatility?: number;
  delta?: number;
  gamma?: number;
  theta?: number;
  vega?: number;
}

const OptionsInformation: React.FC<OptionsInformationProps> = ({
  position,
  optionMarketData,
  formatCurrency,
  formatPercentage,
}) => {
  const [realOptionData, setRealOptionData] = useState<OptionMarketData | null>(
    null,
  );
  const [loadingOptionData, setLoadingOptionData] = useState(false);
  const [optionDataError, setOptionDataError] = useState<string | null>(null);

  // Fetch real option data from Schwab
  useEffect(() => {
    const fetchOptionData = async () => {
      if (
        !position.targetHolding?.optionStrikePrice ||
        !position.targetHolding?.optionExpirationDate
      ) {
        return;
      }

      setLoadingOptionData(true);
      setOptionDataError(null);

      try {
        // Get option chain for the symbol
        const optionChain = await schwabService.getOptionChain(
          position.symbol,
          {
            contractType: position.targetHolding.optionContractType as
              | 'PUT'
              | 'CALL',
            includeUnderlyingQuote: true,
          },
        );

        // Debug: Log the option chain structure
        console.log('Option chain structure:', {
          symbol: position.symbol,
          contractType: position.targetHolding.optionContractType,
          expirationDate: position.targetHolding.optionExpirationDate,
          strikePrice: position.targetHolding.optionStrikePrice,
          callExpDateMap: optionChain.callExpDateMap
            ? Object.keys(optionChain.callExpDateMap)
            : null,
          putExpDateMap: optionChain.putExpDateMap
            ? Object.keys(optionChain.putExpDateMap)
            : null,
        });

        // Log a sample of the actual date keys to see their format
        if (
          optionChain.callExpDateMap &&
          Object.keys(optionChain.callExpDateMap).length > 0
        ) {
          const sampleDateKey = Object.keys(optionChain.callExpDateMap)[0];
          console.log('Sample CALL date key format:', sampleDateKey);
          console.log(
            'Sample CALL contracts for this date:',
            optionChain.callExpDateMap[sampleDateKey],
          );

          // Log all available dates
          console.log(
            'All available CALL dates:',
            Object.keys(optionChain.callExpDateMap),
          );

          // Parse the sample date key to understand the format
          const dateParts = sampleDateKey.split(':');
          console.log('Date key parts:', dateParts);
          console.log('Base date from API:', dateParts[0]);
          console.log('Suffix from API:', dateParts[1]);
        }

        // Find the specific option contract
        const positionExpirationDate = new Date(
          position.targetHolding.optionExpirationDate,
        )
          .toISOString()
          .split('T')[0];
        const strikePrice = position.targetHolding.optionStrikePrice;
        const contractType = position.targetHolding.optionContractType;

        console.log('Looking for contract:', {
          symbol: position.symbol,
          expirationDate: positionExpirationDate,
          strikePrice,
          contractType,
        });

        let targetContract = null;

        // Search in the appropriate expiration map
        if (contractType === 'PUT' && optionChain.putExpDateMap) {
          // Find the exact date match first
          const availableDates = Object.keys(optionChain.putExpDateMap);
          console.log('Available PUT dates:', availableDates);

          // Look for exact date match or date with suffix
          const matchingDateKey = availableDates.find((dateKey) => {
            const baseDate = dateKey.split(':')[0];
            return baseDate === positionExpirationDate;
          });

          if (matchingDateKey) {
            console.log(`Found matching PUT date: ${matchingDateKey}`);
            const putContracts = optionChain.putExpDateMap[matchingDateKey];
            const allContracts = Object.values(putContracts).flat();
            const availableStrikes = allContracts.map(
              (contract: any) => contract.strikePrice,
            );
            console.log(
              `Available PUT strikes for ${matchingDateKey}:`,
              availableStrikes,
            );

            targetContract = allContracts.find(
              (contract: any) => contract.strikePrice === strikePrice,
            );

            if (targetContract) {
              console.log('Found exact PUT contract match:', targetContract);
            } else {
              console.log(`No PUT contract found for strike ${strikePrice}`);
            }
          } else {
            console.log(
              `No PUT date match found for ${positionExpirationDate}`,
            );
          }
        } else if (contractType === 'CALL' && optionChain.callExpDateMap) {
          // Find the exact date match first
          const availableDates = Object.keys(optionChain.callExpDateMap);
          console.log('Available CALL dates:', availableDates);

          // Look for exact date match or date with suffix
          const matchingDateKey = availableDates.find((dateKey) => {
            const baseDate = dateKey.split(':')[0];
            return baseDate === positionExpirationDate;
          });

          if (matchingDateKey) {
            console.log(`Found matching CALL date: ${matchingDateKey}`);
            const callContracts = optionChain.callExpDateMap[matchingDateKey];
            const allContracts = Object.values(callContracts).flat();
            const availableStrikes = allContracts.map(
              (contract: any) => contract.strikePrice,
            );
            console.log(
              `Available CALL strikes for ${matchingDateKey}:`,
              availableStrikes,
            );

            targetContract = allContracts.find(
              (contract: any) => contract.strikePrice === strikePrice,
            );

            if (targetContract) {
              console.log('Found exact CALL contract match:', targetContract);
            } else {
              console.log(`No CALL contract found for strike ${strikePrice}`);
            }
          } else {
            console.log(
              `No CALL date match found for ${positionExpirationDate}`,
            );
          }
        }

        if (targetContract) {
          console.log('Setting real option data:', targetContract);
          setRealOptionData({
            symbol: position.symbol,
            strike: targetContract.strikePrice,
            expiration: positionExpirationDate,
            contractType: targetContract.putCall,
            bid: targetContract.bid,
            ask: targetContract.ask,
            last: targetContract.last,
            volume: targetContract.totalVolume,
            openInterest: targetContract.openInterest,
            impliedVolatility: targetContract.volatility,
            delta: targetContract.delta,
            gamma: targetContract.gamma,
            theta: targetContract.theta,
            vega: targetContract.vega,
          });
        } else {
          const possibleDateKeys = [
            positionExpirationDate,
            `${positionExpirationDate}:1`,
            `${positionExpirationDate}:0`,
          ];
          console.log('No target contract found. Available dates:', {
            callDates: optionChain.callExpDateMap
              ? Object.keys(optionChain.callExpDateMap)
              : [],
            putDates: optionChain.putExpDateMap
              ? Object.keys(optionChain.putExpDateMap)
              : [],
            searchedDates: possibleDateKeys,
            contractType,
            strikePrice,
          });

          // Try to find the closest available contract as a fallback
          let closestContract = null;
          let closestStrikeDiff = Infinity;

          if (contractType === 'CALL' && optionChain.callExpDateMap) {
            // Search through all available dates and strikes
            for (const dateKey of Object.keys(optionChain.callExpDateMap)) {
              const callContracts = optionChain.callExpDateMap[dateKey];
              const allContracts = Object.values(callContracts).flat();

              for (const contract of allContracts) {
                const strikeDiff = Math.abs(contract.strikePrice - strikePrice);
                if (strikeDiff < closestStrikeDiff) {
                  closestStrikeDiff = strikeDiff;
                  closestContract = contract;
                }
              }
            }
          } else if (contractType === 'PUT' && optionChain.putExpDateMap) {
            // Search through all available dates and strikes
            for (const dateKey of Object.keys(optionChain.putExpDateMap)) {
              const putContracts = optionChain.putExpDateMap[dateKey];
              const allContracts = Object.values(putContracts).flat();

              for (const contract of allContracts) {
                const strikeDiff = Math.abs(contract.strikePrice - strikePrice);
                if (strikeDiff < closestStrikeDiff) {
                  closestStrikeDiff = strikeDiff;
                  closestContract = contract;
                }
              }
            }
          }

          if (closestContract) {
            console.log(`Using closest available contract:`, {
              originalStrike: strikePrice,
              closestStrike: closestContract.strikePrice,
              strikeDifference: closestStrikeDiff,
              contract: closestContract,
            });

            setRealOptionData({
              symbol: position.symbol,
              strike: closestContract.strikePrice,
              expiration: new Date(closestContract.expirationDate)
                .toISOString()
                .split('T')[0],
              contractType: closestContract.putCall,
              bid: closestContract.bid,
              ask: closestContract.ask,
              last: closestContract.last,
              volume: closestContract.totalVolume,
              openInterest: closestContract.openInterest,
              impliedVolatility: closestContract.volatility,
              delta: closestContract.delta,
              gamma: closestContract.gamma,
              theta: closestContract.theta,
              vega: closestContract.vega,
            });
          } else {
            setOptionDataError('Option contract not found in chain');
          }
        }
      } catch (error) {
        console.error('Error fetching option data:', error);
        setOptionDataError(
          error instanceof Error
            ? error.message
            : 'Failed to fetch option data',
        );
      } finally {
        setLoadingOptionData(false);
      }
    };

    fetchOptionData();
  }, [position.symbol, position.targetHolding]);

  if (
    position.actualPosition?.positionType !== 'OPTION' ||
    !position.targetHolding
  ) {
    return null;
  }

  const marketDataKey = `${position.symbol}-${position.targetHolding.optionStrikePrice}-${position.targetHolding.optionContractType}-${position.targetHolding.optionExpirationDate}`;
  const mockMarketData = optionMarketData[marketDataKey];

  // Use real data if available, otherwise fall back to mock data
  const marketData = realOptionData || mockMarketData;

  return (
    <div className="mb-6">
      <div className="bg-gradient-to-r from-purple-50 to-pink-50 border border-purple-200 rounded-lg p-4">
        <h3 className="text-sm font-semibold text-purple-800 mb-4 flex items-center">
          <BarChart3 className="w-4 h-4 mr-2" />
          Options Contract Details
        </h3>

        {/* Contract Information */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-4">
          <div className="bg-white border border-purple-200 rounded-lg p-3">
            <p className="text-xs text-purple-600 font-medium mb-1">
              Contract Type
            </p>
            <p className="text-sm font-semibold text-gray-900">
              {position.targetHolding.optionContractType}{' '}
              {position.targetHolding.optionStrikePrice}
            </p>
          </div>

          <div className="bg-white border border-purple-200 rounded-lg p-3">
            <p className="text-xs text-purple-600 font-medium mb-1">
              Expiration Date
            </p>
            <p className="text-sm font-semibold text-gray-900">
              {position.targetHolding.optionExpirationDate
                ? new Date(
                    position.targetHolding.optionExpirationDate,
                  ).toLocaleDateString()
                : 'N/A'}
            </p>
          </div>

          <div className="bg-white border border-purple-200 rounded-lg p-3">
            <p className="text-xs text-purple-600 font-medium mb-1">
              Days to Expiration
            </p>
            <p className="text-sm font-semibold text-gray-900">
              {position.targetHolding.optionExpirationDate
                ? Math.ceil(
                    (new Date(
                      position.targetHolding.optionExpirationDate,
                    ).getTime() -
                      new Date().getTime()) /
                      (1000 * 60 * 60 * 24),
                  )
                : 'N/A'}
            </p>
          </div>

          <div className="bg-white border border-purple-200 rounded-lg p-3">
            <p className="text-xs text-purple-600 font-medium mb-1">Quantity</p>
            <p className="text-sm font-semibold text-gray-900">
              {position.targetHolding.quantity} contracts
            </p>
          </div>

          <div className="bg-white border border-purple-200 rounded-lg p-3">
            <p className="text-xs text-purple-600 font-medium mb-1">
              Expected Fill Price
            </p>
            <p className="text-sm font-semibold text-gray-900">
              {formatCurrency(position.targetHolding.expectedFillPrice)}
            </p>
          </div>

          <div className="bg-white border border-purple-200 rounded-lg p-3">
            <p className="text-xs text-purple-600 font-medium mb-1">
              Total Value
            </p>
            <p className="text-sm font-semibold text-gray-900">
              {formatCurrency(position.targetHolding.totalValue)}
            </p>
          </div>
        </div>

        {/* Current Market Price */}
        {marketData && (
          <div className="bg-white border border-purple-200 rounded-lg p-4 mb-4">
            <div className="flex items-center justify-between mb-3">
              <h4 className="text-xs text-purple-600 font-medium">
                Current Market Price
              </h4>
              {loadingOptionData && (
                <div className="flex items-center text-xs text-purple-600">
                  <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-purple-600 mr-2"></div>
                  Loading...
                </div>
              )}
              {optionDataError && (
                <div className="text-xs text-red-600">{optionDataError}</div>
              )}
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
              <div>
                <p className="text-gray-500">Bid</p>
                <p className="font-semibold text-red-600">
                  {formatCurrency(marketData.bid)}
                </p>
              </div>
              <div>
                <p className="text-gray-500">Ask</p>
                <p className="font-semibold text-green-600">
                  {formatCurrency(marketData.ask)}
                </p>
              </div>
              <div>
                <p className="text-gray-500">Last</p>
                <p className="font-semibold text-gray-900">
                  {formatCurrency(marketData.last)}
                </p>
              </div>
              <div>
                <p className="text-gray-500">Mid Price</p>
                <p className="font-semibold text-blue-600">
                  {formatCurrency((marketData.bid + marketData.ask) / 2)}
                </p>
              </div>
            </div>

            {/* Price Comparison */}
            <div className="mt-3 pt-3 border-t border-gray-200">
              <div className="flex items-center justify-between text-sm">
                <span className="text-gray-500">Expected vs Market:</span>
                <span
                  className={`font-semibold ${
                    position.targetHolding.expectedFillPrice >
                    (marketData.bid + marketData.ask) / 2
                      ? 'text-green-600'
                      : 'text-red-600'
                  }`}
                >
                  {position.targetHolding.expectedFillPrice >
                  (marketData.bid + marketData.ask) / 2
                    ? 'Above Market'
                    : 'Below Market'}
                </span>
              </div>
            </div>
          </div>
        )}

        {/* Risk Metrics */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
          <div className="bg-white border border-purple-200 rounded-lg p-4">
            <h4 className="text-xs text-purple-600 font-medium mb-3">
              Position Metrics
            </h4>
            <div className="grid grid-cols-2 gap-3 text-sm">
              <div>
                <p className="text-gray-500">Side</p>
                <p className="font-semibold text-gray-900">
                  {position.targetHolding.side}
                </p>
              </div>
              <div>
                <p className="text-gray-500">Calculated At</p>
                <p className="font-semibold text-gray-900">
                  {new Date(
                    position.targetHolding.calculatedAt,
                  ).toLocaleDateString()}
                </p>
              </div>
            </div>
          </div>

          <div className="bg-white border border-purple-200 rounded-lg p-4">
            <h4 className="text-xs text-purple-600 font-medium mb-3">
              Risk Metrics
            </h4>
            <div className="grid grid-cols-2 gap-3 text-sm">
              <div>
                <p className="text-gray-500">Allocated Capital</p>
                <p className="font-semibold text-red-600">
                  {formatCurrency(position.allocatedCapital)}
                </p>
              </div>
              <div>
                <p className="text-gray-500">Position Size %</p>
                <p className="font-semibold text-green-600">
                  {formatPercentage(position.positionSizePct)}
                </p>
              </div>
              <div>
                <p className="text-gray-500">Confidence</p>
                <p className="font-semibold text-gray-900">
                  {formatPercentage(position.confidence)}
                </p>
              </div>
              <div>
                <p className="text-gray-500">Risk Budget</p>
                <p className="font-semibold text-gray-900">
                  {formatCurrency(position.riskBudgetAllocation)}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Market Data (if available) */}
        {marketData && (
          <div className="bg-white border border-purple-200 rounded-lg p-4">
            <h4 className="text-xs text-purple-600 font-medium mb-3">
              Market Activity
            </h4>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
              <div>
                <p className="text-gray-500">Volume</p>
                <p className="font-semibold text-gray-900">
                  {marketData.volume.toLocaleString()}
                </p>
              </div>
              <div>
                <p className="text-gray-500">Open Interest</p>
                <p className="font-semibold text-gray-900">
                  {marketData.openInterest.toLocaleString()}
                </p>
              </div>
              <div>
                <p className="text-gray-500">Implied Vol</p>
                <p className="font-semibold text-gray-900">
                  {marketData.impliedVolatility
                    ? `${(marketData.impliedVolatility * 100).toFixed(1)}%`
                    : 'N/A'}
                </p>
              </div>
              <div>
                <p className="text-gray-500">Spread</p>
                <p className="font-semibold text-gray-900">
                  {formatCurrency(marketData.ask - marketData.bid)}
                </p>
              </div>
            </div>

            {/* Greeks (if available) */}
            {marketData.delta !== undefined && (
              <div className="mt-4 pt-4 border-t border-gray-200">
                <h5 className="text-xs text-purple-600 font-medium mb-3">
                  Greeks
                </h5>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                  <div>
                    <p className="text-gray-500">Delta</p>
                    <p className="font-semibold text-gray-900">
                      {marketData.delta?.toFixed(3) || 'N/A'}
                    </p>
                  </div>
                  <div>
                    <p className="text-gray-500">Gamma</p>
                    <p className="font-semibold text-gray-900">
                      {marketData.gamma?.toFixed(4) || 'N/A'}
                    </p>
                  </div>
                  <div>
                    <p className="text-gray-500">Theta</p>
                    <p className="font-semibold text-gray-900">
                      {marketData.theta?.toFixed(3) || 'N/A'}
                    </p>
                  </div>
                  <div>
                    <p className="text-gray-500">Vega</p>
                    <p className="font-semibold text-gray-900">
                      {marketData.vega?.toFixed(3) || 'N/A'}
                    </p>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default OptionsInformation;
